<!--
  A styled select dropdown field with a label, validation, and two-way binding for the selected value.
  
  "options" prop is required, and should be an object with keys representing the human-readable option names to be displayed in the dropdown,
  and values representing the corresponding model values which we're actually binding to.
 
  "allowNullSelection" prop will control whether it's possible to have no option selected - 
  a "Select..." option will appear at the top of the list if this is enabled, and it represents a null bound value.
 
  Note that we do not use Bulma for this select box, as Bulma's select uses non-standard HTML elements / JavaScript,
  rather than an actual <select> element, and so is not very accessible / screen-reader friendly.
 -->
 <template>
  <div class="fieldWrapper">
    <div style="display:flex">
      <label v-if="label" class="label">{{ label }}</label>
      <slot name="top"/>
    </div>

    <p v-if="description" class="mb-2">{{description}}</p>

    <select
      class="selectField"
      v-model="boundValue"
      :disabled="isDisabled"
      :class="{ 'is-danger': validator ? !validator(validationId ? validationId : this, modelValue) : false }"
    >
      <option v-if="allowNullSelection || modelValue == null || modelValue == undefined" :value="undefined">{{ nullSelectionLabel || 'Select...' }}</option>
      <option
        v-for="(optionValue, optionLabel) in allowedOptions"
        :key="optionValue"
        :value="optionValue"
      >{{ optionLabel }}</option>
    </select>
    <slot />
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from "vue";

const props = defineProps<{
  options: object,
  includedOptions?: any[],
  excludedOptions?: any[],
  modelValue: any|null,
  label?: string,
  description?: string,
  allowNullSelection?: boolean,
  nullSelectionLabel?: string,
  validator?: (fieldId: any, val: any) => boolean,
  validationId?: string,
  isDisabled?: boolean
}>();

// Have to bind to the <select> indirectly through this variable, as we can't use it in v-model directly
// Also, the <select> element doesn't let us associate a 'null' value with an <option> - only 'undefined' - so we need to convert between them
const boundValue = ref<any|undefined>(props.modelValue != null ? props.modelValue : undefined);

// Event for two-way binding
const emit = defineEmits(['update:modelValue', 'change'])
watch(boundValue, (val, prevVal) => {
  emit('update:modelValue', val == undefined ? null : val);
  emit('change');
});

watch(()=>props.modelValue, () => {
  boundValue.value = props.modelValue != null ? props.modelValue : undefined;
});

const allowedOptions = computed(() => {
  if (props.includedOptions) {
    return Object.fromEntries(
      Object.entries(props.options).filter(([k,v]) => props.includedOptions!.includes(v))
    );
  } else if (props.excludedOptions) {
    return Object.fromEntries(
      Object.entries(props.options).filter(([k,v]) => !props.excludedOptions!.includes(v))
    );
  } else {
    return props.options;
  }  
});

</script>

<style scoped lang="scss">
@import "@/assets/variables.scss";

/* See: https://www.filamentgroup.com/lab/selectField.html */
.selectField {
  display: block;
  font-size: 16px;
  font-family: $primaryFont;
  font-weight: 500;
  color: #444;
  line-height: 1.2;
  padding: 0.6em 1.4em 0.5em 0.8em;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  margin: 0;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  -moz-appearance: none;
  -webkit-appearance: none;
  appearance: none;
  background-color: #fff;
  background-image: url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23444444%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E"),
    linear-gradient(to bottom, #ffffff 0%, #ffffff 100%);
  background-repeat: no-repeat, repeat;
  background-position: right 0.7em top 50%, 0 0;
  background-size: 0.65em auto, 100%;
}
.selectField::-ms-expand {
  display: none;
}
.selectField:hover {
  border-color: #888;
}
select.is-danger {
  border-color: $danger !important;
}
.selectField:focus {
  border-color: #aaa;
  box-shadow: 0 0 1px 3px rgba(59, 153, 252, 0.7);
  box-shadow: 0 0 0 3px -moz-mac-focusring;
  color: #222;
  outline: none;
}
.selectField option {
  font-weight: normal;
}

/* Support for rtl text, explicit support for Arabic and Hebrew */
*[dir="rtl"] .selectField,
:root:lang(ar) .selectField,
:root:lang(iw) .selectField {
  background-position: left 0.7em top 50%, 0 0;
  padding: 0.6em 0.8em 0.5em 1.4em;
}

/* Disabled styles */
.selectField:disabled,
.selectField[aria-disabled="true"] {
  color: #888;
  cursor: not-allowed;
  border-style: none;
  background-image: url("data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22graytext%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E"),
    linear-gradient(to bottom, whitesmoke 0%, whitesmoke 100%);
}
.selectField:disabled:hover,
.selectField[aria-disabled="true"] {
  border-color: #aaa;
}
</style>
 